import React, { useState } from "react";
import {
  Alert,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { notifySuccess } from "../services/notify";
import {
  ChangePasswordError,
  useChangePassword,
} from "../auth/useChangePassword";

const EMPTY = { current: "", next: "", confirm: "" };

// Opened from the menu under the username, for every signed-in user. The
// passwords go to Flask, which checks and sets them in Logto (see
// useChangePassword).
export const ChangePasswordModal = ({ isOpen, toggle, email }) => {
  const changePassword = useChangePassword();
  const [fields, setFields] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const setField = (name) => (event) => {
    setFields({ ...fields, [name]: event.target.value });
    setError(null);
  };

  // Passwords should not outlive the dialog, so every way out clears them.
  const close = () => {
    if (submitting) return;
    setFields(EMPTY);
    setError(null);
    toggle();
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    // The server never sees the confirmation, so it cannot check this.
    if (fields.next !== fields.confirm) {
      setError("The new passwords do not match.");
      return;
    }
    if (fields.next === fields.current) {
      setError("The new password must be different from your current one.");
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(fields.current, fields.next);
    } catch (caught) {
      setError(
        caught instanceof ChangePasswordError
          ? caught.message
          : "Could not change your password. Please try again."
      );
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    setFields(EMPTY);
    setError(null);
    toggle();
    notifySuccess("Your password was changed");
  };

  const incomplete = !fields.current || !fields.next || !fields.confirm;

  return (
    <Modal isOpen={isOpen} toggle={close} centered>
      <Form onSubmit={onSubmit} data-testid="change-password-form">
        <ModalHeader toggle={close}>Change password</ModalHeader>
        <ModalBody style={{ fontSize: "13px" }}>
          {/* Lets a password manager tie the new password to this account. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={email ?? ""}
            readOnly
            hidden
          />
          <FormGroup>
            <Label for="current-password">Current password</Label>
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={fields.current}
              onChange={setField("current")}
              disabled={submitting}
              autoFocus
            />
          </FormGroup>
          <FormGroup>
            <Label for="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={fields.next}
              onChange={setField("next")}
              disabled={submitting}
            />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label for="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={fields.confirm}
              onChange={setField("confirm")}
              disabled={submitting}
            />
          </FormGroup>
          {error ? (
            <Alert
              color="danger"
              className="mt-3 mb-0"
              style={{ fontSize: "13px" }}
              data-testid="change-password-error"
            >
              {error}
            </Alert>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={close} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={incomplete || submitting}
            style={{ background: "#4A42A0", borderColor: "#4A42A0" }}
          >
            {submitting ? "Changing..." : "Change password"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
